import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'
import type {
  Product,
  ProductCreateInput,
  ProductGateReviewInput,
  ProductListResponse,
  ProductStageHistoryEntry,
  ProductUpdateInput,
} from '../types/product'

interface ProductListFilters {
  focus_area?: string
  lifecycle_stage?: string
  gate_status?: string
  search?: string
  include_archived?: boolean
  page?: number
  page_size?: number
  /** React Query gate for tests that inject preloaded data. */
  enabled?: boolean
}

const QUERY_KEYS = {
  list: (filters: ProductListFilters) => ['products', filters] as const,
  detail: (id: number | string) => ['product', id] as const,
  history: (id: number | string) => ['product-history', id] as const,
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['products'] })
  qc.invalidateQueries({ queryKey: ['product'], exact: false })
}

export function useProducts(filters: ProductListFilters = {}) {
  return useQuery<ProductListResponse>({
    queryKey: QUERY_KEYS.list(filters),
    queryFn: async () => {
      const params: Record<string, string | number> = {}
      if (filters.focus_area) params.focus_area = filters.focus_area
      if (filters.lifecycle_stage) params.lifecycle_stage = filters.lifecycle_stage
      if (filters.gate_status) params.gate_status = filters.gate_status
      if (filters.search) params.search = filters.search
      if (filters.include_archived) params.include_archived = 'true'
      params.page = filters.page ?? 1
      params.page_size = filters.page_size ?? 500
      const res = await api.get('/products', { params })
      return res.data
    },
    staleTime: 30_000,
    enabled: filters.enabled ?? true,
  })
}

export function useProduct(id: number | string | undefined) {
  return useQuery<Product>({
    queryKey: QUERY_KEYS.detail(id ?? ''),
    queryFn: async () => {
      const res = await api.get(`/products/${id}`)
      return res.data
    },
    enabled: !!id,
  })
}

export function useCreateProduct() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: ProductCreateInput) => {
      const res = await api.post('/products', input)
      return res.data as Product
    },
    onSuccess: () => invalidateAll(qc),
  })
}

export function useUpdateProduct() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: ProductUpdateInput }) => {
      const res = await api.patch(`/products/${id}`, body)
      return res.data as Product
    },
    onSuccess: () => invalidateAll(qc),
  })
}

export function useProductHistory(id: number | string | undefined) {
  return useQuery<ProductStageHistoryEntry[]>({
    queryKey: QUERY_KEYS.history(id ?? ''),
    queryFn: async () => {
      const res = await api.get(`/products/${id}/history`)
      return res.data
    },
    enabled: !!id,
  })
}

export function useGateReview() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: ProductGateReviewInput }) => {
      const res = await api.post(`/products/${id}/gate-review`, body)
      return res.data as Product
    },
    onSuccess: (_data, vars) => {
      invalidateAll(qc)
      qc.invalidateQueries({ queryKey: QUERY_KEYS.history(vars.id) })
    },
  })
}
